import EncounterBillingSummaryList from "issara-sdk/apis/EncounterBillingSummaryList_apps_BIL";
import InvoiceItemList from "issara-sdk/apis/InvoiceItemList_apps_BIL";
import OfficialAccountDivisionDetail from "issara-sdk/apis/OfficialAccountDivisionDetail_apps_PRXM";

// serializer
import EncounterBillingSummarySerializer from "issara-sdk/types/EncounterBillingSummarySerializer_apps_BIL";
import InvoiceItemImproveSerializer from "issara-sdk/types/InvoiceItemImproveSerializer_apps_BIL";

// pdf
import FormPaymentSummary, {
  FormPaymentSummaryType,
} from "react-lib/apps/MEH/pdfForm/FormPaymentSummary";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  PickMainState,
} from "react-lib/apps/HISV3/common/CommonInterface";

import moment from "moment";
import { adToBe, formatISOtoBEDateTime } from "react-lib/utils/dateUtils";
import getPdfMake from "react-lib/appcon/common/pdfMake";
import { fetchURLToBase64 } from "react-lib/utils/printURL";
import { formatComma } from "react-lib/utils/tsUtils";
import { OADetailType } from "react-lib/apps/HISV3/common/HeaderPdfFormTemplate";

/** ============== Type-Defining Constants =============  */

export const ACTIONS = {
  SEARCH: "SEARCH",
  GET_PAYMENT_DETAIL: "GET_PAYMENT_DETAIL",
  PRINT_PAYMENT_DETAIL: "PRINT_PAYMENT_DETAIL",
} as const;

export const SEQUENCE_NAME = "PaymentSummary" as const;
export const MAIN_CARD_NAME = "CardPaymentSummary" as const;

/** =============== Types and Interfaces ===============  */
export type State = Partial<{
  selectedPatient?: Record<string, any>;
  selectedEncounter?: Record<string, any>;
  selectedDivision?: Record<string, any>;
  django?: Record<string, any>;
  PaymentSummarySequence?: PaymentSummaryType;
}>;

export type PaymentSummaryType = Partial<{
  sequenceIndex: "Action" | "START" | null;
  billingSummary: EncounterBillingSummarySerializer[];
  billingSummaryFilter: {
    startDate: string;
    endDate: string;
  };
  billingDetail: BillingDetailType;
}> | null;

export type BillingDetailType = {
  item?: InvoiceItemImproveSerializer[];
  detail?: EncounterBillingSummarySerializer;
};

export type PickedState = PickMainState<
  "buttonLoadCheck" | "errorMessage" | "loadingStatus" | "masterOptions"
>;

export type Event = { message: "GetMasterData"; params: Record<string, any> };

export type Data = {
  masterData: any;
  division?: number;
  device?: number;
};

export type SeqState = BaseSeqState<SequenceName>;

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.SEARCH]: { searchText?: string; activePage?: number };
  [ACTIONS.GET_PAYMENT_DETAIL]: { id: number; callback?: (open: boolean) => void };
  [ACTIONS.PRINT_PAYMENT_DETAIL]: { id: number };
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

/** ===================== Constants ====================  */
export const StateInitial: State = {
  PaymentSummarySequence: null,
};

export const DataInitial: Data = {};

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

const Masters = [
  ["province", {}],
  ["district", {}],
  ["city", {}],
] as const;

let toDate = moment();

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.handleEvent({
    message: "GetMasterData",
    params: { masters: Masters },
  });

  const filterStartOfMonth = adToBe(toDate.startOf("month").format("DD/MM/YYYY"));
  const filterEndOfMonth = adToBe(toDate.endOf("month").format("DD/MM/YYYY"));

  controller.setState(
    {
      PaymentSummarySequence: {
        sequenceIndex: "Action",
        billingSummary: [],
        billingSummaryFilter: {
          startDate: filterStartOfMonth,
          endDate: filterEndOfMonth,
        },
      },
    },
    () => Action(controller, { ...params, action: ACTIONS.SEARCH }),
  );
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.SEARCH]: HandleSearch,
    [ACTIONS.GET_PAYMENT_DETAIL]: HandleGetPaymentDetail,
    [ACTIONS.PRINT_PAYMENT_DETAIL]: HandlePrintPaymentDetail,
  };
  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    handler(controller, params);
  } else {
    console.error(`No handler found for action: ${action}`);
  }
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */
const HandleSearch: Handler<Params<"SEARCH">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.LOADING },
  });

  const [billingSummaryRes, billingSummaryErr, billingSummaryNet] =
    await EncounterBillingSummaryList.list({
      params: {
        patient: state.selectedPatient?.patient_id,
        start_date: state.PaymentSummarySequence?.billingSummaryFilter?.startDate,
        end_date: state.PaymentSummarySequence?.billingSummaryFilter?.endDate,
      },
      apiToken: controller.apiToken,
      extra: {
        division: controller.data.division,
      },
    });

  if (billingSummaryRes) {
    controller.setState({
      buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.SUCCESS },
      PaymentSummarySequence: {
        ...state.PaymentSummarySequence,
        billingSummary: billingSummaryRes?.items,
      },
    });
  } else {
    controller.setState({
      buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.ERROR },
    });
  }
};

const HandleGetPaymentDetail: Handler<Params<"GET_PAYMENT_DETAIL">> = async (
  controller,
  params,
) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.GET_PAYMENT_DETAIL]: BLC_STATUS.LOADING,
    },
  });

  const selectData = (state.PaymentSummarySequence?.billingSummary || [])?.find(
    (item: EncounterBillingSummarySerializer) => item.id === params.id,
  );

  const filterDate = formatISOtoBEDateTime(selectData?.created).date;

  const [invoiceRes, invoiceErr, invoiceNet] = await InvoiceItemList.list({
    params: {
      encounter: selectData?.id,
      start_date: filterDate,
      end_date: filterDate,
      exclude_canceled: true,
    },
    apiToken: controller.apiToken,
    extra: {
      division: controller.data.division,
    },
  });

  if (invoiceRes) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.GET_PAYMENT_DETAIL]: BLC_STATUS.SUCCESS,
      },
      PaymentSummarySequence: {
        ...state.PaymentSummarySequence,
        billingDetail: {
          item: invoiceRes?.items || [],
          detail: selectData,
        },
      },
    });

    params.callback?.(true);
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.GET_PAYMENT_DETAIL]: BLC_STATUS.ERROR,
      },
    });

    params.callback?.(false);
  }
};

const HandlePrintPaymentDetail: Handler<Params<"PRINT_PAYMENT_DETAIL">> = async (
  controller,
  params,
) => {
  const state = controller.getState();
  const masterData = controller.data.masterData;

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.PRINT_PAYMENT_DETAIL]: BLC_STATUS.LOADING,
    },
  });

  const selectData = (state.PaymentSummarySequence?.billingSummary || []).find(
    (item: EncounterBillingSummarySerializer) => item.id === params.id,
  );

  const filterDate = formatISOtoBEDateTime(selectData?.created).date;

  const [[oaDetailRes, oaDetailErr, oaDetailNet], [invoiceRes, invoiceErr, invoiceNet]] =
    await Promise.all([
      OfficialAccountDivisionDetail.retrieve({
        params: {},
        extra: { division: controller.data.division || 0 },
        apiToken: controller.apiToken,
      }),
      InvoiceItemList.list({
        params: {
          encounter: selectData?.id,
          start_date: filterDate,
          end_date: filterDate,
          exclude_canceled: true,
        },
        apiToken: controller.apiToken,
        extra: {
          division: controller.data.division,
        },
      }),
    ]);

  let imageFile = "";

  if (oaDetailRes?.image_file) {
    imageFile = await fetchURLToBase64(oaDetailRes?.image_file || "");
  }

  const province = (masterData?.province || [])?.find(
    (item) => item.id === oaDetailRes?.accounting_province,
  );
  const district = (masterData?.district || [])?.find(
    (item) => item.id === oaDetailRes?.accounting_district,
  );
  const city = (masterData?.city || [])?.find((item) => item.id === oaDetailRes?.accounting_city);

  const oaDetail: OADetailType = {
    name: oaDetailRes?.accounting_business_name || "",
    addressTH: oaDetailRes?.accounting_full_address_text || "",
    addressEN: `${city?.name_en || ""} ${
      district?.name_en || ""
    } ${province?.name_en || ""} ${city?.zipcode || ""}`,
    taxPayerID: oaDetailRes?.accounting_tax_payer_id || "",
    imageFile,
  };

  if (invoiceRes) {
    const formatMoney = (value: number | string) => formatComma(String(value));

    let docDef: any = { content: [] };
    const fields = (invoiceRes?.items || []).map((item: InvoiceItemImproveSerializer) => {
      const modeName = (item?.mode_name || "").match(/^(.*?)\s*\((.*?)\)\s*$/);

      const total =
        Number.parseFloat(item.unitprice || "0") * Number.parseFloat(item.quantity || "0");
      const unpaid = total - Number.parseFloat(item.paid || "0");

      return {
        ...item,
        modeNameTH: modeName ? modeName[1].trim() : item?.mode_name || "",
        modeNameEN: modeName ? modeName[2].trim() : "",
        unitprice: formatMoney(item.unitprice),
        price: formatMoney(item.price),
        paid: formatMoney(item.paid),
        total: formatMoney(total),
        unpaid: formatMoney(unpaid),
      };
    });

    const sumNumberField = (items: any[], key: "total" | "discount" | "paid" | "unpaid"): number =>
      items.reduce((sum, item) => sum + Number(String(item?.[key] || "0").replace(/,/g, "")), 0);

    const total = sumNumberField(fields, "total");
    const discount = sumNumberField(fields, "discount");
    const unpaid = sumNumberField(fields, "unpaid");
    const unpaidSum = unpaid - discount;

    const data: FormPaymentSummaryType = {
      hn: state.selectedPatient?.hn,
      patientNameTH: state.selectedPatient?.full_name_th,
      patientNameEN: state.selectedPatient?.full_name_en,
      visitDate: formatISOtoBEDateTime(selectData?.created)?.dateTimeShortBracket,
      encounter: selectData?.number,
      division: state.selectedDivision?.name,

      totalSum: formatMoney(total),
      discountSum: formatMoney(discount),
      grandSum: formatMoney(total - discount),

      paidSum: formatMoney(sumNumberField(fields, "paid")),
      unpaidSum: formatMoney(unpaidSum),

      printedDate: formatISOtoBEDateTime(toDate)?.dateTimeShortBracket,
      printedBy: state.django?.user?.full_name || "",
      oaDetail,
      fields,
    };

    docDef = await FormPaymentSummary(data);

    const pdfMake = await getPdfMake(true);
    pdfMake.createPdf(docDef).open();

    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.PRINT_PAYMENT_DETAIL]: BLC_STATUS.SUCCESS,
      },
    });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.PRINT_PAYMENT_DETAIL]: BLC_STATUS.ERROR,
      },
    });
  }
};
